<template>
	<Slideout v-model:open="vModelOpen">
		<div class="flex flex-col w-full h-full pt-12 px-4 sm:rem:w-[360px]">
			<h2 class="typo-h3 text-heading-secondary">
				Informace o&nbsp;skladovosti
			</h2>
			<p class="mt-4 typo-body-caption text-body-tertiary">
				*&nbsp;Orientačni stav. Dostupnost na prodejně může být jiná.
			</p>
			<FormKit
				v-model="formModel"
				type="form"
				:actions="false"
				:classes="{form: 'space-y-4 mt-4'}"
			>
				<FormKit
					type="text"
					name="search"
					placeholder="Napište např. Praha"
					:classes="{
						inner: 'max-w-none',
						label: 'mb-2 block typo-body-s text-body-tertiary',
						input: 'py-[.6875rem]'
					}"
				>
					<template #prefixIcon>
						<NuxtIcon name="search" class="text-icon-primary text-2xl mr-2" filled />
					</template>
					<template v-if="formModel.search" #suffixIcon>
						<button
							class="inline-flex mr-2 text-body-quaternary hover:text-icon-primary"
							@click="formModel.search = ''"
						>
							<NuxtIcon name="x" class="rem:text-[16px]" filled />
						</button>
					</template>
				</FormKit>
				<FormKit
					type="checkbox"
					name="onlyAvailable"
					aria-label="Zobrazit pouze pobočky s dostupným zbožím"
					:classes="{wrapper: '!mb-0'}"
				>
					<template #decoratorIcon>
						<NuxtIcon name="check" class="text-2xl" filled />
					</template>
					<template #label>
						<span class="typo-body-s text-body-secondary">Zobrazit pouze pobočky s&nbsp;dostupným zbožím</span>
					</template>
				</FormKit>
			</FormKit>
			<Transition name="fade" mode="out-in">
				<div v-if="status === 'pending'" class="my-auto self-center">
					<NuxtIcon
						name="custom/loader"
						class="inline-block text-icon-primary animate-spin rem:text-[32px]"
						filled
					/>
				</div>
				<ul v-else-if="displayedResults.length" class="grow shrink overflow-auto custom-scrollbar border-t border-t-border-secondary mt-8 divide-y divide-border-tertiary py-4">
					<ExpandTransition group>
						<li v-for="item in displayedResults" :key="item.hypermarket.id" class="overflow-hidden">
							<div class="py-4 leading-tight">
								<span class="text-body-secondary font-bold">{{ item.hypermarket.name }}</span><br />
								<span class="typo-body-caption text-body-tertiary">{{ item.hypermarket.address }}</span>
								<div class="flex items-center gap-2 mt-4">
									<ProductAvailabilityIndicator :availability="item.availability" class="m-2" />
									{{ item.availability.description }}
								</div>
							</div>
						</li>
					</ExpandTransition>
				</ul>
				<p v-else class="my-auto text-body-quaternary text-center">
					Nebyly nalezeny žádné výsledky.
				</p>
			</Transition>
		</div>
	</Slideout>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {localeIncludes} from 'locale-includes';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import {GsoaProductCatalogService} from '~/openapi';
	import {ProductAvailability, UnknownProductAvailability} from '~/utils/productAvailability';
	import type {SimplifiedHypermarket} from '~/types/hypermarket';
	import {simplifyHypermarket} from '~/utils';

	type Props = {
		open: boolean;
		vanr: string;
	};

	type Emits = {
		(e: 'update:open', value: boolean): void;
	};

	type FormModel = {
		search: string;
		onlyAvailable: boolean;
	};

	type HypermarketWithAvailability = {
		hypermarket: SimplifiedHypermarket;
		availability: ProductAvailability;
	};

	const props = defineProps<Props>();
	const emit = defineEmits<Emits>();

	const vModelOpen = useVModel(props, 'open', emit);
	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {branches} = storeToRefs(hypermarketsStore);

	const formModel = ref<FormModel>({
		search: '',
		onlyAvailable: false,
	});

	const simplifiedHypermarkets = computed<SimplifiedHypermarket[]>(() => [
		...branches.value?.map(simplifyHypermarket) ?? [],
	]);

	const {data, execute, status} = useAsyncData<HypermarketWithAvailability[] | null>(
		() => Promise.all(
			simplifiedHypermarkets.value.map(async (hypermarket) => {
				const product = await GsoaProductCatalogService.getHouseProductByVanr({
					houseNumber: Number(hypermarket.id),
					productId: props.vanr,
				});

				return {
					hypermarket,
					availability: product
						? ProductAvailability.getAvailability(product)
						: new UnknownProductAvailability(),
				};
			}),
		),
		{immediate: false},
	);

	const displayedResults = computed(() => {
		let results = data.value ?? [];

		if (formModel.value.onlyAvailable) {
			results = results.filter((item) => item.availability.isAvailable === true);
		}

		if (formModel.value.search) {
			results = results.filter((item) => localeIncludes(
				item.hypermarket.name,
				formModel.value.search,
				{sensitivity: 'base'},
			));
		}

		return results;
	});

	whenever(() => props.open && !data.value, () => execute());
</script>
